import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import OpenPlaysLink from "@/components/OpenPlaysLink";
import RentalCaption from "@/components/RentalCaption";
import VenueRentalNotice from "@/components/VenueRentalNotice";
import SwitchCourtDialog from "@/components/SwitchCourtDialog";
import { useDayAvailability } from "@/lib/useDayAvailability";
import { useBookingWindow } from "@/lib/useBookingWindow";
import { Calendar as CalendarIcon, Clock, ArrowLeft, LayoutGrid } from "lucide-react";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { formatSlot } from "@/lib/formatSlot";
import { isSlotPast } from "@/lib/slotTime";

import { API } from "@/lib/apiBase";

const BookingForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { courtId } = useParams();
  const [court, setCourt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [selectedDate, setSelectedDate] = useState(
    location.state?.preselectedDate ? new Date(location.state.preselectedDate + "T00:00:00") : null
  );
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const slotsSectionRef = useRef(null);

  const fetchCourt = useCallback(async () => {
    try {
      const courtRes = await axios.get(`${API}/courts/${courtId}`);
      setCourt(courtRes.data);
    } catch {
      toast.error("Failed to load court details");
    } finally {
      setLoading(false);
    }
  }, [courtId]);

  const fetchAvailableSlots = useCallback(async () => {
    if (!selectedDate) return;
    
    setLoadingSlots(true);
    try {
      const y = selectedDate.getFullYear();
      const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const d = String(selectedDate.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;
      const response = await axios.get(
        `${API}/bookings/court/${courtId}/availability?date=${dateStr}`
      );
      setAllSlots(response.data.all_slots || response.data.available_slots || []);
      // Apply preselected slot(s) from navigation state once
      const preslots = location.state?.preselectedSlots
        || (location.state?.preselectedSlot ? [location.state.preselectedSlot] : null);
      if (preslots) {
        const availSet = new Set(
          (response.data.all_slots || [])
            .filter((s) => s.status === "available")
            .map((s) => s.time_slot)
        );
        const valid = preslots.filter((s) => availSet.has(s));
        setSelectedSlots(valid);
        // Clear the navigation state so it doesn't re-apply
        navigate(location.pathname, { replace: true, state: {} });
      } else {
        setSelectedSlots([]);
      }
    } catch {
      toast.error("Failed to load available time slots");
    } finally {
      setLoadingSlots(false);
    }
  }, [courtId, selectedDate, location.state, location.pathname, navigate]);

  useEffect(() => {
    fetchCourt();
  }, [fetchCourt]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
      // Scroll to Select Time Slots section after render
      const t = setTimeout(() => {
        slotsSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [selectedDate, fetchAvailableSlots]);

  const toggleSlot = (slotTime) => {
    setSelectedSlots((prev) => {
      const orderedTimes = allSlots.map((s) => s.time_slot);

      // Click on an already-selected slot. Per UX spec, this trims the
      // selection to end AT the clicked slot — the clicked slot itself
      // stays, and every slot AFTER it is dropped. Clicking the only
      // remaining slot fully clears the selection.
      if (prev.includes(slotTime)) {
        if (prev.length === 1 && prev[0] === slotTime) return [];
        if (orderedTimes.length === 0) {
          return prev.filter((s) => s !== slotTime);
        }
        const cutIdx = orderedTimes.indexOf(slotTime);
        if (cutIdx === -1) return prev.filter((s) => s !== slotTime);
        return prev.filter((t) => {
          const i = orderedTimes.indexOf(t);
          return i !== -1 && i <= cutIdx;
        });
      }

      // Selection: bridge any gap between the new slot and the existing
      // selection by auto-selecting every AVAILABLE slot in between. This
      // keeps the booking continuous, so a customer who clicks 10 AM and
      // then 2 PM also gets 11 AM, 12 PM, and 1 PM filled automatically
      // (skipping any slot that's booked/unavailable).
      if (allSlots.length === 0) return [...prev, slotTime];

      const newIdx = orderedTimes.indexOf(slotTime);
      if (newIdx === -1) return [...prev, slotTime];

      const prevIndices = prev
        .map((t) => orderedTimes.indexOf(t))
        .filter((i) => i !== -1);
      const allIndices = [...prevIndices, newIdx];
      const minIdx = Math.min(...allIndices);
      const maxIdx = Math.max(...allIndices);

      // Build the bridged set: every available slot index between min..max.
      const bridged = new Set(prev);
      for (let i = minIdx; i <= maxIdx; i++) {
        const s = allSlots[i];
        if (s && s.status === "available") {
          bridged.add(s.time_slot);
        }
      }
      // Always include the just-clicked slot (covers edge cases where the
      // backend status didn't make it into allSlots).
      bridged.add(slotTime);
      // Preserve the canonical order so the summary reads chronologically.
      return orderedTimes.filter((t) => bridged.has(t));
    });
  };

  // Bookable window: today → today + (`windowDays` - 1) inclusive, so the
  // count matches the backend which serves exactly `windowDays` dates
  // (default 30, extensible by admins via the Dashboard "Open next 2 weeks"
  // button).
  const { windowDays } = useBookingWindow();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + Math.max(0, windowDays - 1));

  // Per-day availability for calendar tinting (plenty / few / booked).
  const { byDate, modifiers: availModifiers } = useDayAvailability(today);

  const totalAmount = selectedSlots.reduce((sum, slotTime) => {
    const slot = allSlots.find((s) => s.time_slot === slotTime);
    return sum + (slot?.rate || 0);
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || selectedSlots.length === 0) {
      toast.error("Please select a date and at least one time slot");
      return;
    }
    
    const y = selectedDate.getFullYear();
    const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const d = String(selectedDate.getDate()).padStart(2, "0");
    
    // Pass booking draft to payment page — booking is created there after customer info is filled
    navigate("/payment", {
      state: {
        draft: {
          court_id: courtId,
          court_name: court.name,
          booking_date: `${y}-${m}-${d}`,
          time_slots: selectedSlots,
          total_amount: totalAmount,
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="inline-block w-8 h-8 border-4 border-[#8cc63f] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!court) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-[#a1a1aa]">Court not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1
            className="flex items-center cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img
              src="/assenda-logo.png"
              alt="Assenda"
              className="h-8 sm:h-10 w-auto object-contain"
              data-testid="brand-logo"
            />
          </h1>
          <div className="flex items-center gap-2 sm:gap-3">
            <OpenPlaysLink />
            <button
              data-testid="back-courts-button"
              onClick={() => navigate("/courts")}
              className="text-sm font-semibold text-[#a1a1aa] hover:text-[#8cc63f] transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 sm:pt-28 pb-12 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <h1 className="text-xl sm:text-2xl lg:text-3xl tracking-tighter font-black mb-1">
                  Book <span className="text-[#8cc63f]">{court.name}</span>
                </h1>
              </div>
              <button
                type="button"
                data-testid="all-courts-availability-btn"
                onClick={() => navigate("/availability")}
                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold bg-[#8cc63f]/10 text-[#8cc63f] border border-[#8cc63f]/40 rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors"
              >
                <LayoutGrid size={14} />
                All Courts Availability
              </button>
            </div>
            <RentalCaption />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Row 1 — Select Date (full width now that Switch Court widget is removed) */}
            <div className="grid grid-cols-1 items-start">
              {/* Select Date */}
              <div className="card-surface p-3 sm:p-5 max-w-xl mx-auto w-full">
                <h2 className="text-base sm:text-lg font-bold mb-4 flex items-center justify-center gap-2 px-1">
                  <CalendarIcon size={18} />
                  Select Date
                </h2>
                <div className="assenda-calendar-large">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    fromDate={today}
                    toDate={maxDate}
                    disabled={[
                      { before: today },
                      { after: maxDate },
                      ...availModifiers.booked,
                    ]}
                    modifiers={{
                      plenty: availModifiers.plenty,
                      few: availModifiers.few,
                      booked: availModifiers.booked,
                    }}
                    modifiersClassNames={{
                      selected: "assenda-day-selected",
                      today: "assenda-day-today",
                      plenty: "assenda-day-plenty",
                      few: "assenda-day-few",
                      booked: "assenda-day-booked",
                    }}
                    formatters={{
                      formatDay: (d) => {
                        const y = d.getFullYear();
                        const mo = String(d.getMonth() + 1).padStart(2, "0");
                        const dy = String(d.getDate()).padStart(2, "0");
                        const info = byDate.get(`${y}-${mo}-${dy}`);
                        const title =
                          info && info.total > 0
                            ? info.free === 0
                              ? "Fully booked"
                              : `${info.free} of ${info.total} slots available`
                            : "";
                        return (
                          <span
                            title={title}
                            className="flex flex-col items-center justify-center leading-none"
                          >
                            <span>{d.getDate()}</span>
                            {info && info.total > 0 && (
                              <span
                                data-testid={`day-slot-count-${y}-${mo}-${dy}`}
                                className="mt-0.5 text-[8px] sm:text-[9px] font-semibold tabular-nums text-[#a1a1aa]"
                              >
                                {info.free}/{info.total}
                              </span>
                            )}
                          </span>
                        );
                      },
                    }}
                    className="rounded-md border border-white/10"
                  />
                </div>
                <p className="text-xs text-[#a1a1aa] text-center mt-2 px-1">
                  Mon–Thu: 7 AM – 1 AM · Fri–Sun: 7 AM – 1 AM
                </p>

                {/* Slot-count badge under each date number is the sole
                    availability cue — no separate legend needed. */}
              </div>
            </div>

            {/* Row 2 — Select Time Slots */}
            {selectedDate && (
              <div ref={slotsSectionRef} className="card-surface p-4 scroll-mt-24">
                <div className="flex justify-between items-center mb-3 gap-2">
                  <h2 className="text-base sm:text-lg font-bold flex items-center gap-2">
                    <Clock size={18} />
                    Select Time Slots
                  </h2>
                  {selectedSlots.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#8cc63f] font-semibold">
                        {selectedSlots.length} selected
                      </span>
                      <button
                        type="button"
                        data-testid="clear-selection-button"
                        onClick={() => {
                          setSelectedSlots([]);
                        }}
                        className="text-[10px] px-2.5 py-1 border border-white/15 rounded-sm text-[#a1a1aa] hover:text-white hover:border-red-400 hover:bg-red-400/10 font-semibold uppercase tracking-wider transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {/* Currently Booking badge */}
                <div
                  data-testid="currently-booking-badge"
                  className="mb-3 px-3 py-2 rounded-sm bg-[#8cc63f]/10 border border-[#8cc63f]/40 text-center"
                >
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#8cc63f]">
                      Currently Booking {court.name}
                    </p>
                    <button
                      type="button"
                      data-testid="switch-court-btn"
                      onClick={() => setSwitchOpen(true)}
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border border-[#8cc63f]/50 text-[#8cc63f] hover:bg-[#8cc63f] hover:text-black transition-colors"
                    >
                      <LayoutGrid size={11} />
                      Switch Court
                    </button>
                  </div>
                  {selectedDate && (
                    <p
                      data-testid="currently-booking-date"
                      className="mt-1 text-[10px] uppercase tracking-wider font-semibold text-[#e4e4e7]"
                    >
                      {selectedDate.toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  )}
                </div>

                <p className="text-xs text-[#a1a1aa] mb-3">
                  Tap multiple slots to book consecutive hours.
                </p>
                {(() => {
                  if (loadingSlots) {
                    return (
                      <div className="text-center py-8">
                        <div className="inline-block w-8 h-8 border-4 border-[#8cc63f] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    );
                  }
                  if (allSlots.length === 0) {
                    return (
                      <p className="text-center text-[#a1a1aa] py-8">
                        No slots available for this date. Please choose another date.
                      </p>
                    );
                  }
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {(() => {
                        const dateIso = selectedDate
                          ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
                          : "";
                        return allSlots.map((slot) => {
                          // Past slots on TODAY are visually rendered as
                          // "Past" — non-clickable but not aggressively red,
                          // so customers can still see the missed time.
                          const past = isSlotPast(dateIso, slot.time_slot);
                          const rawStatus = slot.status || "available";
                          const isAvailable = rawStatus === "available" && !past;
                          const isSelected = isAvailable && selectedSlots.includes(slot.time_slot);
                          const blockLabel =
                            past && rawStatus === "available"
                              ? "Past"
                              : rawStatus === "booked"
                              ? "Booked"
                              : rawStatus === "manually_blocked"
                              ? (slot.note || "Blocked")
                              : rawStatus === "externally_blocked"
                              ? (slot.note || "Blocked")
                              : "Blocked";
                          return (
                          <button
                            key={slot.time_slot}
                            type="button"
                            data-testid={`time-slot-${slot.time_slot}`}
                            data-status={past && rawStatus === "available" ? "past" : rawStatus}
                            disabled={!isAvailable}
                            onClick={() => isAvailable && toggleSlot(slot.time_slot)}
                            aria-label={
                              isAvailable
                                ? `Select ${formatSlot(slot.time_slot)}`
                                : `${formatSlot(slot.time_slot)} unavailable (${blockLabel})`
                            }
                            className={`w-full px-2 py-2 border rounded-sm text-xs font-semibold transition-all flex items-center justify-between gap-1 ${
                              !isAvailable
                                ? past && rawStatus === "available"
                                  ? "bg-[#0a0a0a] text-[#525252] border-white/5 cursor-not-allowed"
                                  : "bg-[#1a1a1a] text-[#666] border-white/5 cursor-not-allowed line-through decoration-[#666]"
                                : isSelected
                                ? "bg-[#8cc63f] text-black border-[#8cc63f]"
                                : "bg-[#121212] text-white border-white/10 hover:border-[#8cc63f]"
                            }`}
                          >
                            <span className="whitespace-nowrap no-underline">
                              {formatSlot(slot.time_slot)}
                            </span>
                            {isAvailable ? (
                              <span
                                className={`text-[10px] whitespace-nowrap no-underline ${
                                  isSelected ? "text-black/70" : "text-[#8cc63f]"
                                }`}
                              >
                                ₱{slot.rate}
                              </span>
                            ) : (
                              <span className={`text-[10px] whitespace-nowrap uppercase tracking-wider no-underline ${
                                past && rawStatus === "available" ? "text-[#666]" : "text-[#ff6b6b]"
                              }`}>
                                {blockLabel}
                              </span>
                            )}
                          </button>
                        );
                      });
                      })()}
                    </div>
                  );
                })()}

                {/* See All Courts Availability — alternative entry point to /availability */}
                <div className="mt-4 flex justify-start">
                  <button
                    type="button"
                    data-testid="see-all-courts-availability-btn"
                    onClick={() => navigate("/availability")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider bg-white/[0.04] text-[#a1a1aa] border border-white/15 rounded-sm hover:text-white hover:border-white/30 hover:bg-white/[0.07] transition-colors"
                  >
                    <LayoutGrid size={12} />
                    See All Courts Availability
                  </button>
                </div>
              </div>
            )}

            {/* Submit */}
            <div className="card-surface p-4">
              <div className="mb-4 space-y-3">
                {selectedSlots.length > 0 && (
                  <div className="p-3 bg-[#0a0a0a] border border-white/10 rounded-md">
                    <div className="flex justify-between items-center mb-2">
                      <p className="label-text">Selected Slots</p>
                      <button
                        type="button"
                        data-testid="clear-selection-button-summary"
                        onClick={() => setSelectedSlots([])}
                        className="text-[10px] px-2.5 py-1 border border-white/15 rounded-sm text-[#a1a1aa] hover:text-white hover:border-red-400 hover:bg-red-400/10 font-semibold uppercase tracking-wider transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                    <p
                      data-testid="currently-booking-summary"
                      className="text-[10px] font-bold uppercase tracking-wider text-[#8cc63f] mb-2"
                    >
                      Currently Booking {court.name}
                    </p>
                    {selectedDate && (
                      <p className="text-xs text-white font-semibold mb-2">
                        {selectedDate.toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {selectedSlots.map((s) => (
                        <span key={s} className="text-xs bg-[#8cc63f]/15 text-[#8cc63f] px-2 py-1 rounded-sm font-semibold">
                          {formatSlot(s)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-sm font-semibold text-[#a1a1aa]">Total Amount</span>
                    {selectedSlots.length > 0 && (
                      <p
                        data-testid="selected-hours-count"
                        className="mt-1 leading-none"
                      >
                        <span className="text-3xl font-black text-white tracking-tight">
                          {selectedSlots.length}
                        </span>
                        <span className="ml-1.5 text-xs uppercase tracking-wider font-semibold text-[#a1a1aa]">
                          hour{selectedSlots.length > 1 ? "s" : ""}
                        </span>
                      </p>
                    )}
                  </div>
                  <span className="text-xl font-black text-[#8cc63f]">
                    ₱{totalAmount.toLocaleString()}
                  </span>
                </div>
              </div>
              <button
                data-testid="proceed-payment-button"
                type="submit"
                disabled={submitting || !selectedDate || selectedSlots.length === 0}
                className="btn-primary w-full py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Processing..." : "Continue to book"}
              </button>
            </div>
          </form>

          <VenueRentalNotice />
        </div>
      </main>

      {/* Switch Court popup triggered next to the Currently Booking badge */}
      <SwitchCourtDialog
        open={switchOpen}
        onOpenChange={setSwitchOpen}
        currentCourtId={courtId}
      />
    </div>
  );
};

export default BookingForm;